import { Country, type ProcessorPreferenceKey, ServiceName } from '@consigli/types';
import {
  HiOutlineClipboardDocumentCheck,
  HiOutlineDocumentChartBar,
  HiOutlineDocumentCurrencyDollar,
  HiOutlineDocumentMagnifyingGlass,
  HiOutlineRectangleStack,
  HiOutlineScale,
  HiOutlineShieldExclamation,
  HiOutlineSparkles,
  HiOutlineWrenchScrewdriver,
} from 'react-icons/hi2';

import type { IconType } from 'react-icons';

/**
 * Optional context for gating preview steps. Anything that can influence which
 * steps are shown should live here, so the step definitions stay pure data.
 */
export interface AnalysisPreviewContext {
  hasChecklist: boolean;
  needsClassification?: boolean;
  country?: Country | null;
  /**
   * Processor keys the user picked in the Advanced Control panel. When set
   * (non-empty) and the service supports Advanced Control, processor-mapped
   * steps are filtered to only show the selected ones. Always-on steps with
   * no processor mapping remain visible.
   */
  selectedConflictTypes?: ProcessorPreferenceKey[];
}

export interface AnalysisPreviewStep {
  /** Stable id, used as React key and in test selectors. */
  id: string;
  /** i18n key for the step name (the bold heading). */
  nameKey: string;
  /** i18n key for the step description (the subtle text). */
  descriptionKey: string;
  /** When provided, the step is only included if this returns true. */
  isVisible?: (ctx: AnalysisPreviewContext) => boolean;
  /**
   * Processor keys this step represents. When the service supports Advanced
   * Control and the user has narrowed the processors, only steps that include
   * at least one selected key will be shown.
   */
  processorKeys?: ProcessorPreferenceKey[];
}

export interface AnalysisPreviewSection {
  id: string;
  titleKey: string;
  icon: IconType;
  steps: AnalysisPreviewStep[];
  /** When true, processor-mapped steps inside this section are filtered by Advanced Control. */
  supportsAdvancedControl?: boolean;
}

const stepKeys = (sectionId: string, stepId: string) => ({
  nameKey: `analysis-preview.sections.${sectionId}.steps.${stepId}.name`,
  descriptionKey: `analysis-preview.sections.${sectionId}.steps.${stepId}.description`,
});

const checklistOnlyIfUploaded = (ctx: AnalysisPreviewContext) => ctx.hasChecklist;
const onlyIfNeedsClassification = (ctx: AnalysisPreviewContext) => !!ctx.needsClassification;
const onlyNorwayOrUk = (ctx: AnalysisPreviewContext) =>
  ctx.country === Country.NORWAY || ctx.country === Country.UNITED_KINGDOM;
const onlyNorway = (ctx: AnalysisPreviewContext) => ctx.country === Country.NORWAY;

const documentProcessing: AnalysisPreviewSection = {
  id: 'document-processing',
  titleKey: 'analysis-preview.sections.document-processing.title',
  icon: HiOutlineDocumentMagnifyingGlass,
  steps: [
    { id: 'document-verification', ...stepKeys('document-processing', 'document-verification') },
    { id: 'document-preparation', ...stepKeys('document-processing', 'document-preparation') },
    { id: 'content-extraction', ...stepKeys('document-processing', 'content-extraction') },
    { id: 'digital-indexing', ...stepKeys('document-processing', 'digital-indexing') },
  ],
};

const finalReview: AnalysisPreviewSection = {
  id: 'final-review',
  titleKey: 'analysis-preview.sections.final-review.title',
  icon: HiOutlineSparkles,
  steps: [{ id: 'drafting-summary', ...stepKeys('final-review', 'drafting-summary') }],
};

const tenderAnalysis: AnalysisPreviewSection = {
  id: 'tender-analysis',
  titleKey: 'analysis-preview.sections.tender-analysis.title',
  icon: HiOutlineShieldExclamation,
  supportsAdvancedControl: true,
  steps: [
    {
      id: 'unclear-wording',
      ...stepKeys('tender-analysis', 'unclear-wording'),
      processorKeys: ['UNCLEAR_WORDING', 'UNRESOLVED_ISSUES', 'RESPONSIBILITY'],
    },
    {
      id: 'site-risks',
      ...stepKeys('tender-analysis', 'site-risks'),
      processorKeys: ['SITE_RISK'],
    },
    {
      id: 'contract-risk',
      ...stepKeys('tender-analysis', 'contract-risk'),
      processorKeys: ['CONTRACT_RISK'],
    },
    {
      id: 'standards',
      ...stepKeys('tender-analysis', 'standards'),
      processorKeys: ['WITHDRAWN_STANDARD', 'INTERNATIONAL_STANDARD_REFERENCE'],
    },
    {
      id: 'contradictions',
      ...stepKeys('tender-analysis', 'contradictions'),
      processorKeys: ['CONTRADICTIONS'],
    },
    {
      id: 'missing-document-references',
      ...stepKeys('tender-analysis', 'missing-document-references'),
      processorKeys: ['DOCUMENT_REFERENCES'],
    },
    {
      id: 'deadlines-and-time-periods',
      ...stepKeys('tender-analysis', 'deadlines-and-time-periods'),
      processorKeys: ['UNDEFINED_DEADLINES', 'TIME_PERIODS', 'DEADLINES'],
    },
    {
      id: 'checklist',
      ...stepKeys('tender-analysis', 'checklist'),
      isVisible: checklistOnlyIfUploaded,
    },
  ],
};

const dprAnalysis: AnalysisPreviewSection = {
  id: 'dpr-analysis',
  titleKey: 'analysis-preview.sections.dpr-analysis.title',
  icon: HiOutlineRectangleStack,
  supportsAdvancedControl: true,
  steps: [
    {
      id: 'unclear-wording',
      ...stepKeys('dpr-analysis', 'unclear-wording'),
      processorKeys: ['UNCLEAR_WORDING'],
    },
    {
      id: 'standards',
      ...stepKeys('dpr-analysis', 'standards'),
      processorKeys: ['WITHDRAWN_STANDARD'],
    },
    {
      id: 'contradictions',
      ...stepKeys('dpr-analysis', 'contradictions'),
      processorKeys: ['CONTRADICTIONS'],
    },
    { id: 'missing-drawings', ...stepKeys('dpr-analysis', 'missing-drawings') },
    {
      id: 'missing-drawing-name-in-index',
      ...stepKeys('dpr-analysis', 'missing-drawing-name-in-index'),
    },
    { id: 'revision-mismatch', ...stepKeys('dpr-analysis', 'revision-mismatch') },
    { id: 'title-mismatch', ...stepKeys('dpr-analysis', 'title-mismatch') },
    { id: 'missing-information', ...stepKeys('dpr-analysis', 'missing-information') },
    { id: 'missing-detail', ...stepKeys('dpr-analysis', 'missing-detail') },
    {
      id: 'checklist',
      ...stepKeys('dpr-analysis', 'checklist'),
      isVisible: checklistOnlyIfUploaded,
    },
  ],
};

const rfpAnalysis: AnalysisPreviewSection = {
  id: 'rfp-analysis',
  titleKey: 'analysis-preview.sections.rfp-analysis.title',
  icon: HiOutlineDocumentChartBar,
  steps: [
    { id: 'unclear-wording', ...stepKeys('rfp-analysis', 'unclear-wording') },
    {
      id: 'deadlines-and-time-periods',
      ...stepKeys('rfp-analysis', 'deadlines-and-time-periods'),
    },
    { id: 'timeline', ...stepKeys('rfp-analysis', 'timeline') },
    { id: 'rfp-response-requirements', ...stepKeys('rfp-analysis', 'rfp-response-requirements') },
    { id: 'project-scope-risk', ...stepKeys('rfp-analysis', 'project-scope-risk') },
  ],
};

const momAnalysis: AnalysisPreviewSection = {
  id: 'mom-analysis',
  titleKey: 'analysis-preview.sections.mom-analysis.title',
  icon: HiOutlineWrenchScrewdriver,
  steps: [
    { id: 'editable-documents', ...stepKeys('mom-analysis', 'editable-documents') },
    { id: 'ifc-files', ...stepKeys('mom-analysis', 'ifc-files') },
    { id: 'missing-drawings', ...stepKeys('mom-analysis', 'missing-drawings') },
    { id: 'document-references', ...stepKeys('mom-analysis', 'document-references') },
    {
      id: 'functional-descriptions',
      ...stepKeys('mom-analysis', 'functional-descriptions'),
      isVisible: onlyNorwayOrUk,
    },
    { id: 'missing-requirements', ...stepKeys('mom-analysis', 'missing-requirements') },
    {
      id: 'document-classification',
      ...stepKeys('mom-analysis', 'document-classification'),
      isVisible: onlyIfNeedsClassification,
    },
    {
      id: 'checklist',
      ...stepKeys('mom-analysis', 'checklist'),
      isVisible: checklistOnlyIfUploaded,
    },
    {
      id: 'mandatory-fdv-content',
      ...stepKeys('mom-analysis', 'mandatory-fdv-content'),
      isVisible: onlyNorway,
    },
  ],
};

const bidAnalysis: AnalysisPreviewSection = {
  id: 'bid-analysis',
  titleKey: 'analysis-preview.sections.bid-analysis.title',
  icon: HiOutlineDocumentCurrencyDollar,
  steps: [
    { id: 'unclear-wording', ...stepKeys('bid-analysis', 'unclear-wording') },
    { id: 'site-risks', ...stepKeys('bid-analysis', 'site-risks') },
    { id: 'contract-risk', ...stepKeys('bid-analysis', 'contract-risk') },
    { id: 'standards', ...stepKeys('bid-analysis', 'standards') },
    { id: 'contradictions', ...stepKeys('bid-analysis', 'contradictions') },
    { id: 'missing-references', ...stepKeys('bid-analysis', 'missing-references') },
    { id: 'defined-responsibilities', ...stepKeys('bid-analysis', 'defined-responsibilities') },
    { id: 'checklist', ...stepKeys('bid-analysis', 'checklist') },
  ],
};

const bidLevelingComparison: AnalysisPreviewSection = {
  id: 'bid-leveling-comparison',
  titleKey: 'analysis-preview.sections.bid-leveling-comparison.title',
  icon: HiOutlineScale,
  steps: [
    {
      id: 'auto-fill-leveling-sheet',
      ...stepKeys('bid-leveling-comparison', 'auto-fill-leveling-sheet'),
    },
    {
      id: 'bid-to-bid-comparison',
      ...stepKeys('bid-leveling-comparison', 'bid-to-bid-comparison'),
    },
    { id: 'gap-analysis', ...stepKeys('bid-leveling-comparison', 'gap-analysis') },
  ],
};

const offerFinalReview: AnalysisPreviewSection = {
  id: 'offer-final-review',
  titleKey: 'analysis-preview.sections.offer-final-review.title',
  icon: HiOutlineClipboardDocumentCheck,
  steps: [
    { id: 'quality-assurance', ...stepKeys('offer-final-review', 'quality-assurance') },
    { id: 'report-generation', ...stepKeys('offer-final-review', 'report-generation') },
  ],
};

/**
 * Service-specific section lists for the analysis-preview modal.
 *
 * Document Processing and Final Review are shared; the middle section is the
 * service-specific "what we look for" list. Anything not listed here will not
 * render the preview modal (and therefore not the waiting screen either).
 */
export const ANALYSIS_PREVIEW_STEPS: Partial<Record<ServiceName, AnalysisPreviewSection[]>> = {
  [ServiceName.TENDER_DOCUMENTS]: [documentProcessing, tenderAnalysis, finalReview],
  [ServiceName.DESIGN_PACKAGE_REVIEW]: [documentProcessing, dprAnalysis, finalReview],
  [ServiceName.RFP_RFQ_REVIEW]: [documentProcessing, rfpAnalysis, finalReview],
  [ServiceName.MANAGEMENT_OPERATIONS_MAINTENANCE]: [documentProcessing, momAnalysis, finalReview],
  [ServiceName.OFFER]: [documentProcessing, bidAnalysis, bidLevelingComparison, offerFinalReview],
};

export const ANALYSIS_PREVIEW_SERVICES = Object.keys(ANALYSIS_PREVIEW_STEPS) as ServiceName[];

export const isAnalysisPreviewService = (serviceName: ServiceName | undefined): boolean =>
  !!serviceName && serviceName in ANALYSIS_PREVIEW_STEPS;

const stepMatchesSelectedProcessors = (
  step: AnalysisPreviewStep,
  selected: ProcessorPreferenceKey[] | undefined,
): boolean => {
  // No advanced-control selection means show everything.
  if (!selected || selected.length === 0) return true;
  // Always-on steps (without a processor mapping) stay visible regardless.
  if (!step.processorKeys || step.processorKeys.length === 0) return true;
  return step.processorKeys.some((key) => selected.includes(key));
};

export const getAnalysisPreviewSections = (
  serviceName: ServiceName,
  ctx: AnalysisPreviewContext,
): AnalysisPreviewSection[] => {
  const sections = ANALYSIS_PREVIEW_STEPS[serviceName];
  if (!sections) return [];
  return (
    sections
      .map((section) => ({
        ...section,
        steps: section.steps.filter((step) => {
          if (step.isVisible && !step.isVisible(ctx)) return false;
          if (section.supportsAdvancedControl) {
            return stepMatchesSelectedProcessors(step, ctx.selectedConflictTypes);
          }
          return true;
        }),
      }))
      // Drop sections whose steps were all filtered out so the modal never shows
      // a header that expands to an empty list (e.g. an Advanced Control
      // selection that doesn't intersect any step's processorKeys).
      .filter((section) => section.steps.length > 0)
  );
};

const SERVICE_LABEL_KEYS: Partial<Record<ServiceName, string>> = {
  [ServiceName.TENDER_DOCUMENTS]: 'services.tender-documents',
  [ServiceName.DESIGN_PACKAGE_REVIEW]: 'services.design-package-review',
  [ServiceName.RFP_RFQ_REVIEW]: 'services.rfp-rfq-review',
  [ServiceName.MANAGEMENT_OPERATIONS_MAINTENANCE]: 'services.management-operations-maintenance',
  [ServiceName.OFFER]: 'services.offer',
};

export const getServiceLabelKey = (serviceName: ServiceName): string | undefined =>
  SERVICE_LABEL_KEYS[serviceName];
